/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react'
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  gridPageCountSelector,
  gridPageSelector,
  GridRowModel,
  GridRowsProp,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
  GridColumnVisibilityModel
} from '@mui/x-data-grid'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { Pagination } from '@mui/material'
import { UseReactToPrintFn } from 'react-to-print'
import styles from './Table.module.scss'
import { tableStyles, popperStyles, paginationStyles } from './Table.styles'
import { createCustomToolbar } from './components/CustomGridToolbar/CustomGridToolbar'
import { FILTER_KEYS, TABLE_KEYS } from './models/Filter.model'
import { popperStyles as selectPopperStyles } from '../Select/Select.styles'

interface TableProps {
  columns: GridColDef[]
  columnsModel?: string[]
  hideColumnFilter?: boolean
  hideRowFilter?: boolean
  filtersList?: FILTER_KEYS[]
  tableKey: TABLE_KEYS
  initialRows: GridRowsProp
  filterModel: GridFilterModel
  handleOnAddItem?: () => void
  handleOnDeleteItems?: (ids: string[]) => void
  hideToolbar?: boolean
  hideCheckboxSelection?: boolean
  hideFooter?: boolean
  hideActionsButtons?: boolean
  setSelectedExternalRowIds?: (ids: string[]) => void
  selectedExternalRowIds?: string[]
  handlePrint?: UseReactToPrintFn
  onRowsChange?: (rows: GridRowModel[]) => void
  disablePagination?: boolean
  columnVisibilityModel?: Record<string, boolean>
  isLoading?: boolean
  onRowClick?: (params: any) => void
  getRowClassName?: (params: any) => string
}

function PageNumbers() {
  const apiRef = useGridApiContext()
  const page = useGridSelector(apiRef, gridPageSelector) // 0-based
  const pageCount = useGridSelector(apiRef, gridPageCountSelector) // total pages

  return (
    <Pagination
      count={pageCount}
      page={page + 1}
      onChange={(_e, value) => apiRef.current.setPage(value - 1)}
      sx={paginationStyles}
    />
  )
}

function Table(props: TableProps) {
  const {
    columns,
    // columnsModel,
    hideColumnFilter,
    hideRowFilter,
    initialRows,
    filterModel,
    handleOnAddItem = () => null,
    handleOnDeleteItems = () => null,
    hideToolbar,
    hideCheckboxSelection,
    hideFooter,
    hideActionsButtons,
    setSelectedExternalRowIds,
    selectedExternalRowIds,
    handlePrint,
    onRowsChange,
    disablePagination = false,
    columnVisibilityModel,
    isLoading = false,
    onRowClick,
    getRowClassName
  } = props

  const apiRef = useGridApiRef()
  const [restoreColumnSizes, setRestoreColumnSizes] = useState(false)

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>(
    selectedExternalRowIds || []
  )

  useEffect(() => {
    if (selectedExternalRowIds) {
      setSelectedRowIds(selectedExternalRowIds)
    }
  }, [selectedExternalRowIds])

  const CustomToolbarComponent = useMemo(
    () =>
      createCustomToolbar({
        autosizeOptions: { expand: true },
        apiRef,
        restoreColumnSizes,
        setRestoreColumnSizes,
        hideColumnFilter,
        hideRowFilter,
        handleOnAddItem,
        handleOnDeleteItems,
        selectedRowIds,
        handlePrint,
        hideActionsButtons
      }),
    [
      apiRef,
      restoreColumnSizes,
      setRestoreColumnSizes,
      hideColumnFilter,
      hideRowFilter,
      handleOnAddItem,
      handleOnDeleteItems,
      selectedRowIds,
      handlePrint,
      hideActionsButtons
    ]
  )

  const [visibilityModel, setVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      id: false,
      ...(columnVisibilityModel ?? {})
    })

  useEffect(() => {
    setVisibilityModel(prev => ({
      ...prev,
      ...(columnVisibilityModel ?? {})
    }))
  }, [columnVisibilityModel])

  return (
    <div className={`${styles.tableContainer}`}>
      <DataGrid
        apiRef={apiRef}
        className={`${styles.dataGrid}`}
        rows={initialRows}
        columns={columns}
        columnVisibilityModel={visibilityModel}
        onColumnVisibilityModelChange={model => {
          setVisibilityModel(model)
        }}
        initialState={{
          pagination: { paginationModel: { pageSize: hideFooter ? 100 : 10 } }
        }}
        checkboxSelection={!hideCheckboxSelection || false}
        disableRowSelectionOnClick
        hideFooter={hideFooter || false}
        {...(!disablePagination && {
          pagination: true
        })}
        disableVirtualization
        rowHeight={72}
        columnHeaderHeight={48}
        onColumnResize={() => setRestoreColumnSizes(true)}
        slots={{
          toolbar: !hideToolbar ? CustomToolbarComponent : undefined
        }}
        sx={{
          ...tableStyles,
          width: '100%',
          minWidth: 0,
          '& .MuiDataGrid-main': { minWidth: 0 }
        }}
        pageSizeOptions={[10, 25, 50]}
        loading={isLoading}
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton'
          },
          basePopper: {
            placement: 'bottom-start',
            sx: popperStyles
          },
          pagination: {
            ActionsComponent: PageNumbers,
            SelectProps: {
              IconComponent: ExpandMoreIcon,
              MenuProps: {
                anchorOrigin: { vertical: 'top', horizontal: 'left' },
                transformOrigin: { vertical: 'bottom', horizontal: 'left' },
                PaperProps: { sx: selectPopperStyles }
              }
            }
          }
        }}
        disableColumnResize={false}
        filterModel={filterModel}
        onRowSelectionModelChange={newSelection => {
          setSelectedRowIds(newSelection as string[])
          if (setSelectedExternalRowIds) {
            setSelectedExternalRowIds(newSelection as string[])
          }
        }}
        rowSelectionModel={selectedRowIds}
        processRowUpdate={newRow => {
          const updatedRows = initialRows.map(r =>
            r.id === newRow.id ? newRow : r
          )
          onRowsChange?.(updatedRows)
          return newRow
        }}
        onCellClick={params => {
          if (params.isEditable) {
            apiRef.current.startCellEditMode({
              id: params.id,
              field: params.field
            })
          }
        }}
        onRowClick={onRowClick}
        getRowClassName={getRowClassName}
      />
    </div>
  )
}

export default Table
